import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { formatMoney } from '@/lib/format';
import { CASH_SESSION_STATUS_LABELS, formatPeriodLabel } from '@/lib/cashSession';
import type { CashSession } from '@/lib/types';

interface SessionHistoryProps {
  userId: string;
  branchId: string | null;
  refreshKey: number;
  currencySymbol: string;
  onPrint: (session: CashSession) => void;
}

export default function SessionHistory({
  userId,
  branchId,
  refreshKey,
  currencySymbol,
  onPrint,
}: SessionHistoryProps) {
  const [sessions, setSessions] = useState<CashSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('cash_sessions')
        .select('*, branch:branches(name)')
        .eq('user_id', userId)
        .order('period_date', { ascending: false })
        .limit(30);
      if (branchId) query = query.eq('branch_id', branchId);
      const { data, error: e } = await query;
      if (e) throw e;
      setSessions((data as CashSession[]) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar el historial');
    } finally {
      setLoading(false);
    }
  }, [userId, branchId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <h2 className="font-heading text-lg font-semibold text-foreground-950">
        Mis cuadres anteriores
      </h2>

      {loading ? (
        <div className="mt-6 flex items-center justify-center py-8">
          <div className="w-8 h-8 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      ) : error ? (
        <div className="mt-4 rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-sm text-foreground-700 flex items-center justify-between gap-3">
          <span>{error}</span>
          <button
            onClick={load}
            className="inline-flex items-center gap-1 rounded-md border border-background-300 px-2.5 py-1.5 text-xs font-medium hover:bg-background-100 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-refresh-line" /> Reintentar
          </button>
        </div>
      ) : sessions.length === 0 ? (
        <div className="mt-4 rounded-md border border-dashed border-background-300 p-6 text-center text-sm text-foreground-500">
          Aún no tienes cuadres registrados.
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead className="text-foreground-600 text-left border-b border-background-200">
              <tr>
                <th className="py-2 font-medium">Fecha</th>
                <th className="py-2 font-medium text-center">Ventas</th>
                <th className="py-2 font-medium text-right">Total</th>
                <th className="py-2 font-medium text-right">Ganancia</th>
                <th className="py-2 font-medium text-right">Efectivo esp.</th>
                <th className="py-2 font-medium text-right">Diferencia</th>
                <th className="py-2 font-medium text-center">Estado</th>
                <th className="py-2 font-medium text-right">Acción</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {sessions.map((s) => {
                const diff = s.difference != null ? Number(s.difference) : null;
                return (
                  <tr key={s.id}>
                    <td className="py-2.5 text-foreground-900">
                      {formatPeriodLabel(s.period_date)}
                      {s.branch?.name ? (
                        <span className="block text-xs text-foreground-500">{s.branch.name}</span>
                      ) : null}
                    </td>
                    <td className="py-2.5 text-center text-foreground-700">{s.sales_count}</td>
                    <td className="py-2.5 text-right font-medium text-foreground-950">
                      {formatMoney(Number(s.total), currencySymbol)}
                    </td>
                    <td className="py-2.5 text-right text-accent-700">
                      {formatMoney(Number(s.profit || 0), currencySymbol)}
                      <span className="block text-xs text-foreground-500">
                        {Number(s.margin || 0).toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-2.5 text-right text-foreground-700">
                      {formatMoney(Number(s.cash_expected), currencySymbol)}
                    </td>
                    <td
                      className={`py-2.5 text-right font-medium ${
                        diff != null && Math.abs(diff) >= 0.005 ? 'text-accent-700' : 'text-foreground-700'
                      }`}
                    >
                      {diff == null ? '—' : formatMoney(diff, currencySymbol)}
                    </td>
                    <td className="py-2.5 text-center">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                          s.status === 'closed'
                            ? 'bg-secondary-100 text-secondary-900'
                            : 'bg-accent-100 text-accent-900'
                        }`}
                      >
                        {CASH_SESSION_STATUS_LABELS[s.status]}
                      </span>
                    </td>
                    <td className="py-2.5 text-right">
                      <button
                        onClick={() => onPrint(s)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md text-primary-700 hover:bg-primary-50 cursor-pointer"
                        title="Imprimir cuadre"
                      >
                        <i className="ri-printer-line" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}